import React, { useState, useEffect } from 'react';
import { Button, Modal, message } from 'antd';
import config from '../config';

const CHECK_FOR_UPDATE_INTERVAL_MS = 1000 * 60 * 10;

const getVersionManifestUrl = () => {
  const url = new URL(config.VERSION_MANIFEST_URL, window.location.origin);
  url.searchParams.set('t', Date.now());
  return url.toString();
};

const getManifest = async () => {
  const response = await fetch(getVersionManifestUrl(), {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-store'
    }
  });

  if (!response.ok) {
    throw new Error(`Version manifest request failed with ${response.status}`);
  }

  const manifest = await response.json();

  if (!manifest?.buildId) {
    throw new Error('Version manifest is missing buildId');
  }

  return manifest;
};

export const useVersionUpdater = () => {
  const [updateFound, setUpdateFound] = useState(false);
  const [triggerUpdateCheckAt, setTriggerUpdateCheckAt] = useState(null);
  const [manifestWarningShown, setManifestWarningShown] = useState(false);

  const updateApp = async () => {
    return getManifest()
      .then((manifest) => {
        setManifestWarningShown(false);

        if (config.BUILD_ID === manifest.buildId) return;
        setUpdateFound(true);

        Modal.info({
          title: 'Veritas App Update Found!',
          content: `Click Update to refresh the page and get the latest version${manifest.releaseVersion ? ` (${manifest.releaseVersion})` : ''}`,
          mask: false,
          okText: 'Update',
          cancelButtonProps: { style: { hidden: true } },
          maskClosable: true,
          onOk: () => window.location.reload(),
          onCancel: () => message.info({ 
            duration: null, 
            content: (
              <span>
                <span style={{ marginRight: 10 }}>Outdated versions can cause unexpected behaviors. Click Update to refresh the page and get the latest version</span>
                <Button type='default' onClick={() => window.location.reload()}>Update</Button> 
              </span>
            )
          })
        })
      })
      .catch(() => {
        if (!manifestWarningShown) {
          setManifestWarningShown(true);
          message.warning('Unable to check for the latest app version. The app will retry automatically.');
        }
      });
  }


  useEffect(() => {
    if (!updateFound && !!config.BUILD_ID) {
      let checkUpdateInTenMins;
      updateApp()
        .finally(() => {
          checkUpdateInTenMins = setTimeout(() => setTriggerUpdateCheckAt(Date.now()), CHECK_FOR_UPDATE_INTERVAL_MS) // 10 mins
        });

      return () => clearTimeout(checkUpdateInTenMins);
    }
    // eslint-disable-next-line
  }, [triggerUpdateCheckAt]);
}
